import React from 'react';
import {
  Card,
  Checkbox,
  Radio,
  CardContent,
  Typography,
  Box,
  Stack,
  IconButton,
  Avatar,
  Chip,
  Button
} from '@mui/material';
import Tooltip from '../LibraryTooltip';
import {
  StarBorder as FavoriteBorderIcon,
  Star as FavoritedIcon,
  ShareOutlined as ShareIcon,
  CheckCircleOutlined as CheckCircleIcon,
  LockOutlined as LockIcon
} from '@mui/icons-material';
import { getCommunityCategoryDisplayLabel } from '../../../constants/communityCategories';
import { formatProjectUpdatedLabel } from '../../../constants/projectCategories';
import {
  libraryCardTitleSlotSx,
  libraryCardDescriptionSlotSx,
  libraryCardMetaSx,
} from './libraryCardSlotStyles';
import { libraryCardSx } from '../../../designTokens';
import { getCommunityCategoryBadgeSx } from '../../../constants/communityCategoryColors';
import { resolveCapabilityType } from '../../../constants/builderCategories';
import { getUserDisplayName, getUserInitials } from '../../../utils/userUtils';
import { getPromptGlobalUses, getPromptGlobalFavorites } from '../../../utils/promptEngagement';
import BuildQualityIndicator from './BuildQualityIndicator';

const truncateDescription = (text, max = 140) => {
  if (!text) return '';
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trimEnd()}…`;
};

const formatCompactCount = (value) => {
  const num = Number(value) || 0;
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1).replace(/\.0$/, '')}m`;
  }
  if (num >= 1_000) {
    return `${(num / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  }
  return String(num);
};

const PromptCard = ({
  prompt,
  onPromptClick,
  onFavorite,
  onCopy,
  onShare,
  onMenuOpen,
  onRequestAccess,
  hideStats = false,
  hideActions = false,
  hideMetadata = false,
  hideAuthor = false,
  accessibleLabel,
  compact = false,
  discoveryMode: _discoveryMode = false,
  promptDeepLinkPrefix = '/library',
  onPromptAuxClick,
  onFindMissing,
  selectable = false,
  selected = false,
  singleSelection = false,
  onSelect,
}) => {
  const hasAccess = prompt.hasAccess !== false;

  const handleCardAuxClick = (e) => {
    if (e.button !== 1 || !hasAccess || !prompt?.id) return;
    e.preventDefault();
    if (onPromptAuxClick) {
      onPromptAuxClick(e, prompt);
      return;
    }
    const base = `${window.location.origin}${promptDeepLinkPrefix.replace(/\/$/, '')}`;
    window.open(`${base}/${encodeURIComponent(prompt.id)}`, '_blank', 'noopener,noreferrer');
  };

  const descriptionPreview = truncateDescription(prompt.description || '');
  const descriptionTooltip = (prompt.description || '').trim();
  const authorUser = {
    ...(prompt.author || {}),
    name: prompt.author?.name && prompt.author.name !== 'Unknown' ? prompt.author.name : (prompt.userName || prompt.author?.name || prompt.userId),
    email: prompt.author?.email || prompt.userEmail,
    id: prompt.author?.id || prompt.userId,
  };
  const authorName = getUserDisplayName(authorUser);
  const updatedLabel = formatProjectUpdatedLabel(prompt.updatedAt || prompt.createdAt);
  const categoryLabel = getCommunityCategoryDisplayLabel(prompt.category);
  const resolvedType = resolveCapabilityType(prompt.capabilityType);
  const TypeIcon = resolvedType.Icon;
  const cardPadding = compact ? 1.25 : 1.75;

  const useCount = getPromptGlobalUses(prompt);
  const favoriteCount = getPromptGlobalFavorites(prompt);

  return (
    <Card
      role={accessibleLabel ? 'button' : undefined}
      aria-label={accessibleLabel}
      aria-disabled={accessibleLabel && !hasAccess ? true : undefined}
      tabIndex={accessibleLabel && hasAccess ? 0 : undefined}
      onKeyDown={accessibleLabel ? (event) => {
        if (event.target === event.currentTarget && hasAccess && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onPromptClick?.(prompt);
        }
      } : undefined}
      sx={{
        ...libraryCardSx,
        ...(accessibleLabel && { '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 3 } }),
        height: '100%',
        cursor: hasAccess ? 'pointer' : 'default',
        opacity: hasAccess ? 1 : 0.85,
        position: 'relative',
        ...(compact && {
          maxWidth: { xs: 360, sm: 'none' },
          mx: { xs: 'auto', sm: 0 },
        }),
        ...(!hasAccess && {
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            pointerEvents: 'none',
            zIndex: 1,
          },
        }),
      }}
      onClick={() => hasAccess && onPromptClick && onPromptClick(prompt)}
      onAuxClick={handleCardAuxClick}
    >
      <CardContent
        sx={{
          flexGrow: 1,
          p: cardPadding,
          '&:last-child': { pb: cardPadding },
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {selectable && React.createElement(singleSelection ? Radio : Checkbox, {
          checked: selected, disabled: !hasAccess,
          inputProps: { 'aria-label': `Select ${prompt.title || prompt.name || 'Untitled capability'}` },
          onClick: (event) => event.stopPropagation(),
          onChange: () => onSelect?.(prompt),
          sx: { position: 'absolute', top: 4, right: 4, zIndex: 3 },
        })}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: compact ? 0.625 : 0.875, gap: 0.75 }}>
          <Tooltip title={`${resolvedType.label}: ${resolvedType.hint}`} arrow>
            <Chip
              label={(
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.375,
                    minWidth: 0,
                    lineHeight: 1,
                  }}
                >
                  <TypeIcon
                    sx={{
                      fontSize: '12px !important',
                      width: 12,
                      height: 12,
                      display: 'block',
                      flexShrink: 0,
                      transform: 'translateY(-1px)',
                    }}
                  />
                  <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {prompt.category ? categoryLabel : resolvedType.label}
                  </Box>
                </Box>
              )}
              size="small"
              variant="outlined"
              sx={{
                ...getCommunityCategoryBadgeSx(prompt.category || 'other'),
                maxWidth: selectable ? 'calc(100% - 34px)' : '100%',
                '& .MuiChip-label': {
                  display: 'inline-flex',
                  alignItems: 'center',
                  lineHeight: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  px: 0.875,
                },
              }}
              aria-label={`${prompt.category ? categoryLabel : resolvedType.label} — ${resolvedType.label}: ${resolvedType.hint}`}
            />
          </Tooltip>

          {!hideActions && hasAccess && (
            <Stack direction="row" spacing={0} sx={{ flexShrink: 0, mt: -0.25 }}>
              {onFavorite && (
                <IconButton
                  size="small"
                  aria-label="Favorite"
                  onClick={(e) => {
                    e.stopPropagation();
                    onFavorite(prompt.id);
                  }}
                  sx={{ p: 0.375, '& .MuiSvgIcon-root': { fontSize: 16 } }}
                >
                  {prompt.isFavorited
                    ? <FavoritedIcon sx={{ color: '#d4a017' }} />
                    : <FavoriteBorderIcon sx={{ color: 'text.disabled' }} />}
                </IconButton>
              )}
              {onShare && (
                <IconButton
                  size="small"
                  aria-label="Share"
                  onClick={(e) => {
                    e.stopPropagation();
                    onShare(prompt);
                  }}
                  sx={{ p: 0.375, '& .MuiSvgIcon-root': { fontSize: 16 } }}
                >
                  <ShareIcon sx={{ color: 'text.disabled' }} />
                </IconButton>
              )}
            </Stack>
          )}
        </Box>

        <Box sx={{ mb: compact ? 0.625 : 0.875 }}>
          <Typography
            component="h3"
            variant="h6"
            sx={libraryCardTitleSlotSx()}
          >
            {prompt.title}
          </Typography>
        </Box>

        <Tooltip
          title={descriptionTooltip}
          placement="top"
          arrow
          disableHoverListener={!descriptionTooltip}
        >
          <Typography
            component="p"
            variant="body2"
            sx={{
              ...libraryCardDescriptionSlotSx(compact),
              mb: compact ? 0.625 : 1,
            }}
          >
            {descriptionPreview || '\u00a0'}
          </Typography>
        </Tooltip>

        {!hideStats && (!hideActions || selectable) && hasAccess && <Box sx={{ minHeight: 28, mb: 0.5, display: 'flex', alignItems: 'center' }}>
          <BuildQualityIndicator showLabel buildQuality={prompt.buildQuality} onFindMissing={onFindMissing ? () => onFindMissing(prompt) : undefined} />
        </Box>}

        {!hideStats && (
          <Box
            sx={{
              mb: compact ? 0.75 : 1.25,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 0.75,
              minWidth: 0,
              fontSize: compact ? '0.6875rem' : '0.75rem',
              lineHeight: compact ? 1.35 : 1.5,
            }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 0.5, rowGap: 0.25, minWidth: 0 }}>
              <Box
                component="span"
                sx={{ fontWeight: 400, color: 'rgb(100, 116, 139)' }}
                aria-label={`Uses: ${useCount}`}
              >
                {formatCompactCount(useCount)} Uses
              </Box>
              <Box component="span" sx={{ color: 'rgb(203, 213, 225)', userSelect: 'none' }}>
                ·
              </Box>
              <Box
                component="span"
                sx={{ fontWeight: 400, color: 'rgb(100, 116, 139)' }}
                aria-label={`Favorites: ${favoriteCount}`}
              >
                {formatCompactCount(favoriteCount)} Favorites
              </Box>
            </Box>
          </Box>
        )}

        {!hideMetadata && <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 0.75,
            mt: 'auto',
            pt: compact ? 0.125 : 0.375,
          }}
        >
          {!hideAuthor && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625, minWidth: 0 }}>
            <Avatar
              sx={{
                width: compact ? 18 : 20,
                height: compact ? 18 : 20,
                fontSize: '0.59375rem',
                bgcolor: 'rgba(29, 78, 216, 0.12)',
                color: 'primary.main',
                flexShrink: 0,
              }}
            >
              {getUserInitials(authorUser)}
            </Avatar>
            <Typography
              component="span"
              noWrap
              sx={{
                fontSize: compact ? '10.5px' : '11.5px',
                fontWeight: 500,
                color: 'rgb(100, 116, 139)',
                lineHeight: 1.4,
                minWidth: 0,
              }}
            >
              {authorName}
              {prompt.author?.verified && (
                <Tooltip title="Verified Creator">
                  <CheckCircleIcon
                    sx={{ fontSize: 12, ml: 0.5, verticalAlign: 'text-bottom', color: 'primary.main' }}
                  />
                </Tooltip>
              )}
            </Typography>
          </Box>}
          {updatedLabel && (
            <Typography component="span" noWrap sx={{ ...libraryCardMetaSx, flexShrink: 0 }}>
              Updated {updatedLabel}
            </Typography>
          )}
        </Box>}

        {!hasAccess && onRequestAccess && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<LockIcon />}
            onClick={(e) => {
              e.stopPropagation();
              onRequestAccess(prompt);
            }}
            sx={{ width: '100%', mt: 2 }}
          >
            Request Access
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default PromptCard;
